import React from 'react';
import {
  Menu,
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  Chip,
  Divider,
  Badge,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  MarkAsUnread,
  Delete,
  MoreVert,
  Notifications,
  CheckCircle,
  Warning,
  Error,
  Info,
} from '@mui/icons-material';
import { Notification } from '@/entities/ui';

interface NotificationDropdownProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  notifications: Notification[];
  onMarkAsRead?: (id: string) => void;
  onDelete?: (id: string) => void;
  onMarkAllRead?: () => void;
  onViewAll?: () => void;
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'success':
      return <CheckCircle fontSize="small" color="success" />;
    case 'warning':
      return <Warning fontSize="small" color="warning" />;
    case 'error':
      return <Error fontSize="small" color="error" />;
    default:
      return <Info fontSize="small" color="info" />;
  }
};

const getNotificationTypeColor = (type: string) => {
  switch (type) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'default';
  }
};

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  anchorEl,
  open,
  onClose,
  notifications,
  onMarkAsRead,
  onDelete,
  onMarkAllRead,
  onViewAll,
}) => {
  const unreadCount = notifications.filter(n => !n.read).length;

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  const handleDelete = (event: React.MouseEvent, id: string) => {
    event.stopPropagation();
    if (onDelete) {
      onDelete(id);
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: 320, sm: 360 },
          maxHeight: 480,
          mt: 0.5,
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <Box
        sx={{
          p: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Notifications fontSize="small" color="primary" />
          <Typography
            variant="subtitle1"
            sx={{ fontSize: '0.9375rem', fontWeight: 600 }}
          >
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Badge
              badgeContent={unreadCount}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.6875rem',
                  height: 16,
                  minWidth: 16,
                },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <Button
                size="small"
                startIcon={<MarkAsUnread fontSize="small" />}
                onClick={onMarkAllRead}
                sx={{
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  height: 28,
                }}
              >
                Mark all
              </Button>
            </Tooltip>
          )}
          <Tooltip title="More options">
            <IconButton size="small" sx={{ p: 0.5, width: 28, height: 28 }}>
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Notification List */}
      <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
        {notifications.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Notifications sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 1,
                    px: 1.5,
                    cursor: 'pointer',
                    backgroundColor: notification.read
                      ? 'transparent'
                      : 'action.hover',
                    borderLeft: notification.read ? 'none' : '3px solid',
                    borderLeftColor:
                      getNotificationTypeColor(notification.type) === 'success'
                        ? 'success.main'
                        : getNotificationTypeColor(notification.type) ===
                            'warning'
                          ? 'warning.main'
                          : getNotificationTypeColor(notification.type) ===
                              'error'
                            ? 'error.main'
                            : 'primary.main',
                    '&:hover': {
                      backgroundColor: 'action.selected',
                    },
                    transition: 'all 0.15s ease-in-out',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    {/* Icon/Avatar */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: notification.read
                          ? 'grey.100'
                          : 'primary.50',
                        flexShrink: 0,
                        mt: 0.5,
                      }}
                    >
                      {notification.avatar ? (
                        <Avatar
                          src={notification.avatar}
                          sx={{ width: 24, height: 24 }}
                        />
                      ) : (
                        getNotificationIcon(notification.type)
                      )}
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontSize: '0.8125rem',
                            fontWeight: notification.read ? 400 : 600,
                            color: notification.read
                              ? 'text.secondary'
                              : 'text.primary',
                            lineHeight: 1.3,
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Chip
                          label={notification.type}
                          size="small"
                          color={
                            getNotificationTypeColor(notification.type) as any
                          }
                          sx={{
                            fontSize: '0.625rem',
                            height: 18,
                            '& .MuiChip-label': { px: 0.5 },
                          }}
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          lineHeight: 1.4,
                          mb: 0.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {notification.message}
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.6875rem',
                            color: 'text.disabled',
                          }}
                        >
                          {notification.time}
                        </Typography>
                        {notification.user && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.6875rem',
                              color: 'text.disabled',
                              fontStyle: 'italic',
                            }}
                          >
                            by {notification.user}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {!notification.read && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Tooltip title="Delete notification">
                        <IconButton
                          size="small"
                          onClick={e => handleDelete(e, notification.id)}
                          sx={{
                            p: 0.5,
                            width: 24,
                            height: 24,
                            opacity: 0.7,
                            '&:hover': { opacity: 1 },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </ListItem>
                {index < notifications.length - 1 && <Divider sx={{ mx: 1 }} />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Footer */}
      {notifications.length > 0 && (
        <Box
          sx={{
            p: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Button
            size="small"
            variant="text"
            onClick={onViewAll}
            sx={{
              fontSize: '0.8125rem',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            View all notifications
          </Button>
        </Box>
      )}
    </Menu>
  );
};
